import { motion } from "framer-motion";
import { Plus, Minus, RotateCcw } from "lucide-react";

interface DhikrItem {
  id: string;
  name: string;
  arabicName: string;
  target: number;
}

interface DhikrTrackerProps {
  day: number;
  counts: Record<number, Record<string, number>>; // day -> dhikr_id -> count
  onUpdateCount: (day: number, dhikrId: string, count: number) => void;
}

const DHIKR_ITEMS: DhikrItem[] = [
  { id: "subhanallah", name: "SubhanAllah", arabicName: "سُبْحَانَ اللَّهِ", target: 33 },
  { id: "alhamdulillah", name: "Alhamdulillah", arabicName: "الْحَمْدُ لِلَّهِ", target: 33 },
  { id: "allahuakbar", name: "Allahu Akbar", arabicName: "اللَّهُ أَكْبَرُ", target: 34 },
  { id: "salawat", name: "Salawat", arabicName: "صَلَّى اللَّهُ عَلَيْهِ وَسَلَّمَ", target: 100 },
  { id: "istighfar", name: "Astaghfirullah", arabicName: "أَسْتَغْفِرُ اللَّهَ", target: 100 },
  { id: "lailahaillallah", name: "La ilaha illallah", arabicName: "لَا إِلَٰهَ إِلَّا اللَّهُ", target: 100 },
];

export const DhikrTracker = ({ day, counts, onUpdateCount }: DhikrTrackerProps) => {
  const todayCounts = counts[day] || {};

  const getCount = (dhikrId: string) => todayCounts[dhikrId] || 0;

  const handleIncrement = (dhikrId: string) => {
    onUpdateCount(day, dhikrId, getCount(dhikrId) + 1);
  };

  const handleDecrement = (dhikrId: string) => {
    const current = getCount(dhikrId);
    if (current > 0) {
      onUpdateCount(day, dhikrId, current - 1);
    }
  };

  const handleReset = (dhikrId: string) => {
    onUpdateCount(day, dhikrId, 0);
  };

  const getTotalDhikr = () => {
    return Object.values(todayCounts).reduce((sum, count) => sum + count, 0);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
          Day {day}
        </span>
        <span className="text-xs text-muted-foreground">
          Total: {getTotalDhikr()} dhikr
        </span>
      </div>

      <div className="space-y-3">
        {DHIKR_ITEMS.map((dhikr) => {
          const count = getCount(dhikr.id);
          const progress = Math.min((count / dhikr.target) * 100, 100);
          const isComplete = count >= dhikr.target;

          return (
            <motion.div
              key={dhikr.id}
              className={`bg-background border rounded-sm p-4 transition-colors ${
                isComplete ? "border-gold/50 bg-gold/5" : "border-border"
              }`}
              layout
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground/90">{dhikr.name}</p>
                  <p className="font-arabic text-lg text-gold/80">{dhikr.arabicName}</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={() => handleDecrement(dhikr.id)}
                    className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors"
                    whileTap={{ scale: 0.9 }}
                  >
                    <Minus className="w-4 h-4" />
                  </motion.button>
                  
                  <motion.button
                    onClick={() => handleIncrement(dhikr.id)}
                    className="w-16 h-16 rounded-full bg-gold/10 border-2 border-gold/30 flex items-center justify-center 
                               hover:bg-gold/20 hover:border-gold/50 transition-all text-2xl font-display text-gold"
                    whileTap={{ scale: 0.95 }}
                  >
                    {count}
                  </motion.button>
                  
                  <motion.button
                    onClick={() => handleReset(dhikr.id)}
                    className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors"
                    whileTap={{ scale: 0.9 }}
                    title="Reset"
                  >
                    <RotateCcw className="w-3 h-3" />
                  </motion.button>
                </div>
              </div>

              {/* Progress bar */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1 bg-border rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${isComplete ? "bg-emerald-500" : "bg-gold"}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {count}/{dhikr.target}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
